/**
 * `FitResult` (plan §4.3, test-plan §1a): the classify-and-fit output of one analysis.
 *
 * This is the stored, family-agnostic shape (`params` holds each family's block by id). The
 * strict structured-output schema, with every registered family's parameter block, is
 * assembled in `fit.ts` from the registry. Kept free of registry imports so the domain
 * schemas can use it.
 */
import { z } from 'zod';

/** Confidence at or above which a fit is a proposal (plan §4.3; starting value, §13). */
export const PROPOSE_AT = 0.7;
/** Confidence at or above which a fit is still proposed but flagged with its runners-up. */
export const FLAG_AT = 0.4;

export const FitCandidateSchema = z.object({
  familyId: z.string().min(1),
  /** 0–1. */
  confidence: z.number().min(0).max(1),
  why: z.string(),
});
export type FitCandidate = z.infer<typeof FitCandidateSchema>;

export const RecognisedFactSchema = z.object({
  kind: z.string(),
  value: z.union([z.number(), z.string()]),
  unit: z.string().nullable(),
  source: z.enum(['text', 'image']),
});

export const TextSpanSchema = z.object({ kind: z.string(), span: z.tuple([z.int().nonnegative(), z.int().nonnegative()]), text: z.string() });

/** Proportions and appearance read from the photo; 0 or '' = could not tell. */
export const SharedFitSchema = z.object({
  widthOverHeight: z.number().min(0).describe('Overall width divided by overall height as seen on the frontal photo; 0 if you cannot tell.'),
  depthOverWidth: z.number().min(0).describe('Depth divided by width; 0 if you cannot tell.'),
  /** Visible board thickness over W (two-stage intake; absent in older results). */
  boardThicknessOverWidth: z.number().min(0).optional(),
  material: z.string().describe('Material in plain words, or empty.'),
  colour: z.string().describe('Colour in plain words, or empty.'),
  finish: z.string().describe('Finish in plain words, or empty.'),
});

export const PointSchema = z.object({ x: z.number(), y: z.number() });

/**
 * FND-16: the four corners of the piece's FRONT face on the measured photo, normalised image
 * coordinates (0,0 top left), in the order top-left, top-right, bottom-right, bottom-left. A
 * point (u, v) of the front elevation (u = x / W from the left, v = y / H from the top) maps to
 * the photo through the homography of these corners (src/families/overlay.ts).
 */
export const FrontQuadSchema = z.array(PointSchema)
  .describe('The four corners of the piece\'s FRONT face on the measured photo, as fractions of the image width and height (0,0 = top left), in this order: top-left, top-right, bottom-right, bottom-left. On an angled photo the quadrilateral is not a rectangle: follow the front edges as they recede. Exactly 4 points.');

const FitEvidenceBase = {
  /** The piece's outer edges on the measured photo, as fractions of its width and height (y = 0 at the top). */
  carcass: z.object({ left: z.number(), right: z.number(), top: z.number(), bottom: z.number() })
    .describe('The piece\'s outer left, right, top and bottom edges on the measured photo as fractions of the image width and height (0,0 = top left). The whole frame (0, 1, 0, 1) when nothing fits.'),
  /** Which photo the fit was measured on (1-based). */
  imageIndex: z.int().min(1),
  textSpans: z.array(TextSpanSchema),
};

/** Stored evidence: `frontQuad` and `viewYawDeg` are absent in results recorded before FND-16. */
export const FitEvidenceSchema = z.object({
  ...FitEvidenceBase,
  frontQuad: FrontQuadSchema.optional(),
  /** Camera yaw against the front face, degrees: 0 frontal, negative when seen from the left, positive from the right. */
  viewYawDeg: z.number().optional(),
});

/** The fit stage's evidence (Claude must give the front quadrilateral and the yaw). */
export const FitEvidenceOutputSchema = z.object({
  ...FitEvidenceBase,
  frontQuad: FrontQuadSchema,
  viewYawDeg: z.number().describe('Camera yaw against the front face in degrees: 0 for a frontal photo, negative when the camera stands to the left of the piece, positive to the right; roughly, from how much the side shows.'),
});

/**
 * What the photo shows as structure (stage 1), compared with the rebuilt boards afterwards
 * (FND-05, FND-06: the fidelity check). Counts are of boards seen on the front.
 */
export const StructureSchema = z.object({
  verticalBoards: z.int().min(0),
  horizontalBoards: z.int().min(0),
  fullHeightSides: z.int().min(0).max(2),
  verticalsAlign: z.enum(['yes', 'no', 'none']),
  layout: z.enum(['regular', 'irregular']),
  freeShelfEnds: z.int().min(0),
});
export type Structure = z.infer<typeof StructureSchema>;

/** Things on the piece Cutlist Maker does not rebuild yet (FND-09): reported, never modelled. */
export const FeaturesSchema = z.object({
  doors: z.int().min(0),
  drawers: z.int().min(0),
  slattedSides: z.boolean(),
  legs: z.boolean(),
});
export type Features = z.infer<typeof FeaturesSchema>;

/** The post-fit fidelity check (FND-05, FND-06): how closely the rebuilt boards follow the photo. */
export const FidelitySchema = z.object({
  /** 0–1: 1 when the rebuilt structure matches what the photo shows. */
  score: z.number().min(0).max(1),
  /** One British-English sentence for Check when the rebuild differs from the photo. */
  warning: z.string().optional(),
  /** What differs, in plain words (for the warning and the runners-up). */
  reasons: z.array(z.string()).optional(),
});
export type Fidelity = z.infer<typeof FidelitySchema>;

/**
 * The FitResult as Claude returns it and as it is stored. Kept free of nullable unions (the
 * structured-output grammar has a size budget): `chosen` is a family id or "none",
 * `noFitReason` is empty when a family fits, unknown proportions are 0.
 */
export const FitResultSchema = z.object({
  candidates: z.array(FitCandidateSchema),
  chosen: z.string(),
  fitsAFamily: z.boolean(),
  noFitReason: z.string(),
  /** What the photo shows, in plain words, with the side-by-side bays counted (0 if not shelving). */
  seen: z.object({ description: z.string(), bays: z.int().nonnegative() }),
  /** One block per family id; only the chosen family's block is meaningful. */
  params: z.record(z.string(), z.unknown()),
  shared: SharedFitSchema,
  recognisedFacts: z.array(RecognisedFactSchema),
  evidence: FitEvidenceSchema,
  evidenceNotes: z.array(z.string()),
  // --- pilot findings (all optional: absent in results recorded before them) --------------------
  /** Stage 1's structural reading of the photo. */
  structure: StructureSchema.optional(),
  /** Stage 1's doors, drawers, slats and legs. */
  features: FeaturesSchema.optional(),
  /** Stage 2: visible structure the chosen family's parameters cannot express. */
  notCaptured: z.array(z.string()).optional(),
  /** Set after the fit (FND-09): what the photo shows that the rebuild leaves out, e.g. ["2 doors", "1 drawer"]. */
  unmodelledFeatures: z.array(z.string()).optional(),
  /** Set after the fit (FND-05, FND-06). */
  fidelity: FidelitySchema.optional(),
});
export type FitResult = z.infer<typeof FitResultSchema>;

export const isFitResult = (p: unknown): p is FitResult => !!p && typeof p === 'object' && Array.isArray((p as FitResult).candidates) && 'fitsAFamily' in (p as object);

export type FitDecision =
  | { kind: 'proposal'; familyId: string; confidence: number; flagged: boolean; runnersUp: FitCandidate[] }
  | { kind: 'no-fit'; reason: string; candidates: FitCandidate[] };

/**
 * Thresholds (plan §4.3): ≥ 0.70 proposal; 0.40–0.69 proposal flagged with the runners-up
 * first; below 0.40, `fitsAFamily: false`, or a chosen family that is not registered → no-fit.
 */
export function decideFit(fit: FitResult, knownFamilies: readonly string[]): FitDecision {
  const ranked = fit.candidates.filter((c) => knownFamilies.includes(c.familyId)).slice().sort((a, b) => b.confidence - a.confidence);
  const chosenId = fit.chosen !== 'none' && knownFamilies.includes(fit.chosen) ? fit.chosen : null;
  const chosen = chosenId ? ranked.find((c) => c.familyId === chosenId) ?? null : null;
  const confidence = chosen?.confidence ?? 0;
  const runnersUp = ranked.filter((c) => c.familyId !== chosenId);
  if (!fit.fitsAFamily || !chosenId || confidence < FLAG_AT || fit.params[chosenId] == null) {
    return { kind: 'no-fit', reason: fit.noFitReason, candidates: ranked };
  }
  return { kind: 'proposal', familyId: chosenId, confidence, flagged: confidence < PROPOSE_AT, runnersUp };
}

/** `BoardSketch` (test-plan §1a, internal): the spike's board graph, stored for taxonomy work, never shown. */
export const BoardSketchSchema = z.object({
  isCaseFurniture: z.boolean(),
  styleFamily: z.string(),
  envelopeAspect: z.object({ widthOverHeight: z.number(), depthOverWidth: z.number() }),
  boards: z.array(z.object({
    id: z.string(),
    role: z.enum(['side', 'divider', 'shelf', 'top', 'bottom', 'back', 'plinth', 'end', 'post', 'block', 'other']),
    x0: z.number(), x1: z.number(), y0: z.number(), y1: z.number(),
    depthFraction: z.number(),
    joins: z.array(z.object({ to: z.string(), kind: z.enum(['butt', 'housed-dado', 'mitre', 'cantilever-overhang', 'unknown']), where: z.string() })),
    confidence: z.number(),
    note: z.string(),
  })),
  openEnds: z.object({ left: z.boolean(), right: z.boolean(), back: z.boolean(), description: z.string() }),
  material: z.object({ guess: z.string(), colour: z.string(), visibleGrain: z.boolean(), finish: z.string() }),
  styleCues: z.array(z.string()),
  uncertainties: z.array(z.string()),
});
export type BoardSketch = z.infer<typeof BoardSketchSchema>;
